import { Box, Button, Chip, Divider, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { GameResult, opposingTeam, PlayerInfo, TeamId, rankToCode } from "../interface/GameTypes";
import type { MatchPlayerNetPayload } from "../sockets-client";
import { usePostHog } from "@posthog/react";

const SUIT_SYMBOLS: Record<string, string> = {
  Spades: "♠", Hearts: "♥", Diamonds: "♦", Clubs: "♣",
};
const SUIT_COLORS: Record<string, string> = {
  Spades: "#e0e0e0", Clubs: "#e0e0e0", Hearts: "#f44336", Diamonds: "#f44336",
};

interface Props {
  result: GameResult;
  myTeam: TeamId | null;
  players: PlayerInfo[];
  onPlayAgain: () => void;
  matchTeamPoints?: { A: number; B: number };
  matchGamesPlayed?: number;
  matchPlayerPoints?: MatchPlayerNetPayload[];
  /** Leave the room (server confirms with `LeftRoom`). */
  onLeaveRoom?: () => void;
}

export const GameOverScreen = ({
  result,
  myTeam,
  players,
  onPlayAgain,
  matchTeamPoints,
  matchGamesPlayed,
  matchPlayerPoints,
  onLeaveRoom,
}: Props) => {
  const posthog = usePostHog();
  const [showRounds, setShowRounds] = useState(true);

  const winnerTeam: TeamId = result.oppositionWon ? opposingTeam(result.callerTeam) : result.callerTeam;

  const iWon = myTeam === winnerTeam;

  useEffect(() => {
    posthog?.capture("game_over", {
      winner_team: winnerTeam,
      caller_team: result.callerTeam,
      opposition_won: result.oppositionWon,
      caller_team_marks: result.callerTeamMarks,
      opposition_marks: result.oppositionMarks,
      trump_suit: result.trumpSuit,
      stake: result.stake,
      i_won: iWon,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const oppositionTeam: TeamId = opposingTeam(result.callerTeam);
  const winTarget = result.winTarget ?? Math.round(result.totalMarks / 3);
  const teamAMarks = result.callerTeam === TeamId.A ? result.callerTeamMarks : result.oppositionMarks;
  const teamBMarks = result.callerTeam === TeamId.B ? result.callerTeamMarks : result.oppositionMarks;

  const trumpSym = result.trumpSuit ? (SUIT_SYMBOLS[result.trumpSuit] ?? result.trumpSuit) : null;
  const trumpColor = result.trumpSuit ? (SUIT_COLORS[result.trumpSuit] ?? "#fff") : "#fff";
  const playerBySeat = (seatIndex: number) => players.find((p) => p.seatIndex === seatIndex);

  const roundDetails = result.roundSummary && result.roundSummary.length > 0 ? (
    <>
      <Button
        variant="text"
        size="small"
        onClick={() => setShowRounds((prev) => !prev)}
        sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", mb: 0.5, textTransform: "none" }}
      >
        {showRounds ? "Hide round details" : "Show round details"}
      </Button>
      {showRounds && (
        <Box sx={{
          maxHeight: { xs: "min(42vh, 280px)", md: 260 },
          overflowY: "auto",
          borderRadius: 1.5,
          border: "1px solid rgba(255,255,255,0.1)",
          backgroundColor: "rgba(0,0,0,0.3)",
          width: "100%",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255,255,255,0.15)", borderRadius: 2 },
        }}>
          {result.roundSummary!.map((r) => {
            const tc = r.winnerTeam === TeamId.A ? "#4caf50" : "#ffc107";
            const wn = playerBySeat(r.winnerIndex)?.name ?? `Seat ${r.winnerIndex}`;
            return (
              <Box key={r.roundNumber} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 0.4, borderBottom: "1px solid rgba(255,255,255,0.06)", "&:last-child": { borderBottom: "none" } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.6rem", width: 14, textAlign: "right", flexShrink: 0 }}>{r.roundNumber}</Typography>
                  <Box sx={{ display: "flex", gap: "3px", flexWrap: "wrap", flex: 1, minWidth: 0 }}>
                    {r.cards.map((pc, ci) => (
                      <Typography key={ci} variant="caption" sx={{ color: SUIT_COLORS[pc.card.suit] ?? "#fff", fontSize: "0.65rem", fontFamily: "monospace", lineHeight: 1.1 }}>
                        {rankToCode[pc.card.rank] ?? pc.card.rank}{SUIT_SYMBOLS[pc.card.suit] ?? pc.card.suit}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ color: tc, fontSize: "0.6rem", fontWeight: "bold", maxWidth: 50, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wn}</Typography>
                  <Typography variant="caption" sx={{ color: "#fff", fontSize: "0.7rem", fontWeight: "bold", minWidth: 20, textAlign: "right" }}>
                    {r.points}{r.isLastRound && r.lastRoundBonus > 0 && <Box component="span" sx={{ color: "#ffd700", fontSize: "0.55rem" }}>*</Box>}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  ) : null;

  const fmtSigned = (n: number) => `${n >= 0 ? "+" : ""}${n}`;

  const matchLine =
    matchTeamPoints != null ? (
      <Box sx={{ mb: 0.5 }}>
        <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.7rem", textAlign: "center" }}>
          Match totals — A: {matchTeamPoints.A} · B: {matchTeamPoints.B}
          {typeof matchGamesPlayed === "number" ? ` · Deals: ${matchGamesPlayed}` : null}
        </Typography>
        {matchPlayerPoints && matchPlayerPoints.length > 0 ? (
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.65rem", textAlign: "center", mt: 0.35 }}>
            Individual (match net):{" "}
            {matchPlayerPoints.map((r, i) => (
              <span key={r.uuid}>
                {i > 0 ? " · " : ""}
                {r.name ?? `Seat ${r.seatIndex}`} {fmtSigned(r.net)}
              </span>
            ))}
          </Typography>
        ) : null}
      </Box>
    ) : null;

  const actionButtons = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center", width: "100%" }}>
      {matchLine}
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          posthog?.capture("play_again_selected", { mode: "same_seats" });
          onPlayAgain();
        }}
        sx={{ minWidth: 140, fontSize: "0.85rem" }}
      >
        Play again
      </Button>
      {onLeaveRoom && (
        <Button variant="text" color="inherit" onClick={onLeaveRoom} sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.75rem", textTransform: "none" }}>
          Leave room
        </Button>
      )}
    </Box>
  );

  const summaryBlockMd = (
    <>
      <Typography sx={{
        fontSize: "3rem", mb: 0.5, lineHeight: 1,
        animation: iWon ? "bounce-in 0.6s ease-out" : "shake 0.5s ease-in-out 0s 2",
        "@keyframes bounce-in": { "0%": { transform: "scale(0)", opacity: 0 }, "60%": { transform: "scale(1.3)" }, "100%": { transform: "scale(1)", opacity: 1 } },
        "@keyframes shake": { "0%, 100%": { transform: "translateX(0)" }, "25%": { transform: "translateX(-8px)" }, "75%": { transform: "translateX(8px)" } },
      }}>
        {iWon ? "🎉" : "👎"}
      </Typography>
      <Typography variant="h4" sx={{ color: "white", mb: 1, fontWeight: "bold" }}>Game Over</Typography>
      <Typography variant="h5" sx={{ color: iWon ? "#4caf50" : "#f44336", mb: 2, fontWeight: "bold" }}>{iWon ? "You Win!" : "You Lose!"}</Typography>
      <Chip label={`Team ${winnerTeam} wins`} color={winnerTeam === TeamId.A ? "success" : "warning"} sx={{ mb: 3 }} />
      <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", mb: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "space-around", mb: 2 }}>
        <Box>
          <Typography variant="caption" color="rgba(255,255,255,0.6)">Team A</Typography>
          <Typography variant="h6" color="white">{teamAMarks}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="rgba(255,255,255,0.6)">Team B</Typography>
          <Typography variant="h6" color="white">{teamBMarks}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="body2" color="rgba(255,255,255,0.6)">Calling team: Team {result.callerTeam}</Typography>
        {trumpSym && <Typography variant="body2" sx={{ color: trumpColor, fontWeight: "bold" }}>Trump: {trumpSym} {result.trumpSuit}</Typography>}
      </Box>
      <Typography variant="body2" color="rgba(255,255,255,0.6)" sx={{ mb: 0.5 }}>
        {result.oppositionWon
          ? `Opposition (Team ${oppositionTeam}) scored ${result.oppositionMarks} — reached the ${winTarget} mark target`
          : `Opposition (Team ${oppositionTeam}) scored ${result.oppositionMarks} — below the ${winTarget} mark target`}
      </Typography>
      {result.lastRoundBonus > 0 && (
        <Typography variant="body2" color="rgba(255,255,255,0.45)" sx={{ mb: 0.5, fontSize: "0.75rem" }}>Last round bonus: +{result.lastRoundBonus} pts</Typography>
      )}
      <Typography variant="body2" sx={{ color: "#ffd700", mb: 2 }}>Stake: {result.stake}</Typography>
    </>
  );

  const summaryBlockXs = (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: "2.25rem", lineHeight: 1 }}>{iWon ? "🎉" : "👎"}</Typography>
        <Box sx={{ textAlign: "left" }}>
          <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "1.35rem", lineHeight: 1.15 }}>Game Over</Typography>
          <Typography sx={{ color: iWon ? "#4caf50" : "#f44336", fontWeight: "bold", fontSize: "1rem" }}>{iWon ? "You Win!" : "You Lose!"}</Typography>
        </Box>
      </Box>
      <Chip label={`Team ${winnerTeam} wins`} size="small" color={winnerTeam === TeamId.A ? "success" : "warning"} sx={{ mt: 0.5 }} />
      <Box sx={{ display: "flex", gap: 4, justifyContent: "center", mt: 1.5 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem" }}>Team A</Typography>
          <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "1.35rem", lineHeight: 1 }}>{teamAMarks}</Typography>
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem" }}>Team B</Typography>
          <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "1.35rem", lineHeight: 1 }}>{teamBMarks}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center", flexWrap: "wrap", mt: 1.25 }}>
        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.78rem" }}>Caller: Team {result.callerTeam}</Typography>
        {trumpSym && (
          <Typography sx={{ color: trumpColor, fontWeight: "bold", fontSize: "0.78rem" }}>
            Trump: {trumpSym} {result.trumpSuit}
          </Typography>
        )}
      </Box>
      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.78rem", textAlign: "center", mt: 1, lineHeight: 1.45, px: 0.5 }}>
        {result.oppositionWon
          ? `Opposition (Team ${oppositionTeam}) scored ${result.oppositionMarks} — reached the ${winTarget} mark target`
          : `Opposition (Team ${oppositionTeam}) scored ${result.oppositionMarks} — below the ${winTarget} mark target`}
      </Typography>
      {result.lastRoundBonus > 0 && (
        <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", mt: 0.5 }}>Last round bonus: +{result.lastRoundBonus} pts</Typography>
      )}
      <Typography sx={{ color: "#ffd700", fontSize: "0.85rem", fontWeight: 700, mt: 1 }}>Stake: {result.stake}</Typography>
    </>
  );

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        backgroundColor: "#1a1a2e",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* ── Phone / tablet: one column, full viewport scroll (portrait + landscape) ── */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: "rgba(0,0,0,0.35)",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            px: { xs: 1.5, sm: 2.5 },
            pt: { xs: "max(16px, env(safe-area-inset-top, 0px))", sm: "max(20px, env(safe-area-inset-top, 0px))" },
            pb: { xs: "max(28px, env(safe-area-inset-bottom, 0px))", sm: "max(32px, env(safe-area-inset-bottom, 0px))" },
            maxWidth: 520,
            width: "100%",
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
            {summaryBlockXs}
            <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", width: "100%", my: 2 }} />
            <Box sx={{ width: "100%", textAlign: "left" }}>
              {roundDetails ?? (
                <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.8rem", textAlign: "center" }}>No round data</Typography>
              )}
            </Box>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", width: "100%", my: 2 }} />
            {actionButtons}
          </Box>
        </Box>
      </Box>

      {/* ── Desktop layout (md+) — scroll when content exceeds short viewports (e.g. iPad landscape) ── */}
      <Box sx={{
        display: { xs: "none", md: "flex" },
        flex: 1,
        minHeight: 0,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.88)",
        p: { md: 2 },
        pt: "max(12px, env(safe-area-inset-top, 0px))",
        pb: "max(12px, env(safe-area-inset-bottom, 0px))",
        boxSizing: "border-box",
      }}>
        <Box sx={{
          backgroundColor: "#1a1a2e",
          borderRadius: 3,
          padding: { md: 3, lg: 4 },
          minWidth: { md: 280, lg: 320 },
          maxWidth: 500,
          width: "100%",
          maxHeight: "min(92dvh, 92vh)",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          textAlign: "center",
          border: "1px solid rgba(255,255,255,0.15)",
          boxSizing: "border-box",
        }}>
          {summaryBlockMd}
          {roundDetails}
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 2 }} />
          {actionButtons}
        </Box>
      </Box>
    </Box>
  );
};
